import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/api";

export default function PaymentSuccessPage() {
  const { refreshUser } = useAuth();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [pd, setPd] = useState(null);
  const nav = useNavigate();
  const sid = sp.get("session_id");

  useEffect(() => {
    if (!sid) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 5) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (cancelled) return;
        setPd(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          await refreshUser();
          toast.success("Payment successful!");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (err) {
        logger.error("Poll payment status", err);
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sid, refreshUser]);

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 mb-2 font-heading">Processing...</h2>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4" data-testid="payment-success-icon">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 mb-2 font-heading">Payment Successful!</h2>
            <p className="text-zinc-500 mb-4">{pd?.credits} credits added.</p>
            <button data-testid="back-to-dashboard-btn" onClick={() => nav("/dashboard")} className="bg-brand text-white font-semibold px-8 py-3 hover:bg-brand-hover">Dashboard</button>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-black tracking-tighter text-zinc-950 mb-2 font-heading">Payment Issue</h2>
            <button data-testid="retry-pricing-btn" onClick={() => nav("/pricing")} className="bg-brand text-white font-semibold px-8 py-3 hover:bg-brand-hover mt-4">Try Again</button>
          </>
        )}
      </div>
    </AppShell>
  );
}
